import type { EvaluatedValue, Input, InputId, NodeId } from 'web';

/**
 * The shape every evaluated media value shares: either a real file, which carries the durable asset
 * id, or something computed on the fly, which carries no provenance at all.
 */
type EvaluatedMediaPart = { type: 'file'; id: string | undefined } | { type: 'computed' };

/**
 * Mirrors `LINEAGE_MAX_WALK_DEPTH` in the backend's `lineage-node-classification`. Duplicated rather
 * than imported because the client cannot reach into `weavy/core`.
 */
const MAX_WALK_DEPTH = 32;

/**
 * The slice of `FlowGraph` the walk needs.
 *
 * Narrow on purpose: it keeps the walk unit-testable, since a real `FlowGraph` under vitest has no
 * wasm instance and every `wasm.call` returns `undefined`.
 */
export interface LineageGraphPort {
  getNodeInput(nodeId: NodeId, inputId: InputId): Input | undefined;
  getInputIds(nodeId: NodeId): InputId[];
  evaluateOutput(input: Input): EvaluatedValue | undefined;
}

/**
 * Durable asset ids behind a node input, for the audit trail.
 *
 * A graphics hop (crop, blur, resize, ...) discards provenance: `EvaluatedImage::into_computed` keeps
 * only width and height, so by the time a rendered image reaches a model node it carries no asset id
 * and the audit row has nothing to name as its parent. The server cannot recover it either — a
 * single-node run ships only the model node, so the edges back to the upload are filtered out before
 * capture sees them.
 *
 * The client can, because its wasm graph is always the whole workflow. This walks upstream until each
 * branch reaches a value backed by a real file and returns those ids, which the caller stamps onto
 * the request as `lineageSourceAssetIds`.
 *
 * Resolution is per-run by construction: every terminus is read through `evaluateOutput`, the same
 * path the run itself uses, so an Import yields the file it currently has selected and a model node
 * yields its selected generation. Deliberately not read off `data.files[data.selectedIndex]` — that
 * is a single mutable value shared by every run, which is exactly the bug this replaces.
 */
export function resolveLineageSourceAssetIds(graph: LineageGraphPort, nodeId: NodeId, inputId: InputId): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  collectFromInput(graph, nodeId, inputId, ids, seen, new Set<NodeId>(), 0);
  return ids;
}

/**
 * Attaches resolved source ids to a value bound for the server.
 *
 * Writes both the singular and plural keys because the backend reads both — `extractIdsFromValue` in
 * `lineage-input-snapshot.utils` and `extractDurableAssetIds` in `lineage-upstream-walk.utils`.
 * Returns the value untouched when there is nothing to add, so callers need no guard.
 */
export function withLineageSourceAssetIds<T>(value: T, sourceAssetIds: string[]): T {
  if (sourceAssetIds.length === 0 || value == null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  return { ...value, lineageSourceAssetId: sourceAssetIds[0], lineageSourceAssetIds: sourceAssetIds };
}

function collectFromInput(
  graph: LineageGraphPort,
  nodeId: NodeId,
  inputId: InputId,
  ids: string[],
  seen: Set<string>,
  visiting: Set<NodeId>,
  depth: number,
): void {
  if (depth >= MAX_WALK_DEPTH) {
    return;
  }

  const input = graph.getNodeInput(nodeId, inputId);
  if (!input || visiting.has(input.nodeId)) {
    return;
  }

  const evaluated = graph.evaluateOutput(input);
  if (!evaluated) {
    return;
  }

  const parts = mediaPartsOf(evaluated);
  if (!parts) {
    // Not a media value, so there is no asset behind it and nothing upstream worth walking.
    return;
  }

  const fileIds = parts.flatMap((part) => (part.type === 'file' && part.id ? [part.id] : []));
  if (fileIds.length > 0) {
    // A real file terminates this branch: it is the asset that was consumed.
    for (const id of fileIds) {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
    return;
  }

  // Everything here is computed, so keep walking. Every input is followed rather than just the first,
  // so a compositing node reports each of its sources.
  visiting.add(input.nodeId);
  for (const upstreamInputId of graph.getInputIds(input.nodeId)) {
    collectFromInput(graph, input.nodeId, upstreamInputId, ids, seen, visiting, depth + 1);
  }
}

/**
 * The file-or-computed parts of a media value, or `undefined` when the value is not media at all.
 *
 * The composites are flattened because each of their images can independently be a real file or a
 * computed render, and any that is a file names an asset the run consumed.
 */
function mediaPartsOf(evaluated: EvaluatedValue): EvaluatedMediaPart[] | undefined {
  switch (evaluated.type) {
    case 'image':
    case 'video':
    case 'audio':
    case '3D':
      return [evaluated.value];
    case 'kling_element':
      return [
        evaluated.value.frontalImageUrl,
        evaluated.value.referenceImageUrl1,
        evaluated.value.referenceImageUrl2,
        evaluated.value.referenceImageUrl3,
      ].filter((image): image is NonNullable<typeof image> => image != null);
    case 'keyframe':
      return [evaluated.value.imageUrl];
    default:
      return undefined;
  }
}
